"use client";
import {
	CognitoIdentity,
	type GetCredentialsForIdentityCommandOutput,
} from "@aws-sdk/client-cognito-identity";
import {
	AdminListGroupsForUserCommand,
	CognitoIdentityProviderClient,
} from "@aws-sdk/client-cognito-identity-provider";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import { Amplify } from "aws-amplify";
import {
	type AuthUser,
	type ConfirmResetPasswordInput,
	type ConfirmSignInOutput,
	confirmResetPassword,
	confirmSignIn,
	decodeJWT,
	fetchAuthSession,
	getCurrentUser,
	type ResetPasswordInput,
	type ResetPasswordOutput,
	resetPassword,
	type SignInInput,
	type SignInOutput,
	signIn,
	signOut,
} from "aws-amplify/auth";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { COGNITO_CONFIG, COGNITO_IDPOOL_ID } from "@/config/constants";

// biome-ignore lint/style/noNamespace: cognito error names namespace needed for error handling
export namespace cognitoErrorNames {
	export enum SignIn {
		UserNotFound = "UserNotFoundException",
		NotAuthorized = "NotAuthorizedException",
		UserAlreadyAuthenticated = "UserAlreadyAuthenticatedException",
	}
	export enum ConfirmSignIn {
		UserNotFound = "UserNotFoundException",
		UserNotConfirmed = "UserNotConfirmedException",
		NotAuthorized = "NotAuthorizedException",
		InvalidPassword = "InvalidPasswordException",
		PasswordMismatch = "PasswordMismatchError",
	}
	export enum ResetPassword {
		UserNotFound = "UserNotFoundException",
		LimitExceeded = "LimitExceededException",
		NotAuthorized = "NotAuthorizedException",
		InvalidParameter = "InvalidParameterException",
	}
	export enum ConfirmResetPassword {
		UserNotFound = "UserNotFoundException",
		CodeMismatch = "CodeMismatchException",
	}
	export enum GetCurrentUser {
		UserUnAuthenticatedException = "UserUnAuthenticatedException",
	}
}

// biome-ignore lint/style/noNamespace: cognito error names namespace needed for error handling
export namespace cognitoErrorMessages {
	export enum SignIn {
		Incorrect = "Incorrect username or password.",
		UserDisabled = "User is disabled.",
		TemporaryPasswordExpired = "Temporary password has expired and must be reset by an administrator.",
	}
	export enum ConfirmSignIn {
		Incorrect = "Incorrect username or password.",
		UserDisabled = "User is disabled.",
		PasswordMismatch = "新しいパスワードと確認用パスワードが一致しません。",
	}
}

interface ConfirmSignInInput {
	username: string;
	tempPassword: string;
	newPassword: string;
	newConfirmPassword: string;
}

interface CurrentAuthenticatedUser {
	authUser: AuthUser;
	isAuthenticated: boolean;
}

export interface UseAuth {
	email: string;
	authUser: AuthUser | null;
	usergroup: string;
	isAuthenticated: boolean | null;
	handleSignIn: ({ username, password }: SignInInput) => Promise<SignInOutput>;
	handleConfirmSignIn: ({
		username,
		tempPassword,
		newPassword,
		newConfirmPassword,
	}: ConfirmSignInInput) => Promise<ConfirmSignInOutput>;
	handleSignOut: () => Promise<void>;
	handleResetPassword: ({ username }: ResetPasswordInput) => Promise<ResetPasswordOutput>;
	handleConfirmResetPassword: ({
		username,
		confirmationCode,
		newPassword,
	}: ConfirmResetPasswordInput) => Promise<void>;
	currentAuthenticatedUser: () => Promise<CurrentAuthenticatedUser | undefined>;
	fetchCredentials: () => Promise<GetCredentialsForIdentityCommandOutput>;
}

const AuthContext = createContext<UseAuth>({} as UseAuth);

export const useAuth = () => {
	return useContext(AuthContext);
};

interface AuthProviderProps {
	children: React.ReactNode;
}

export const InternalAuthProvider = ({ children }: AuthProviderProps) => {
	useEffect(() => {
		Amplify.configure({
			Auth: {
				Cognito: {
					...COGNITO_CONFIG,
					loginWith: { username: true, email: true },
				},
			},
		});
	}, []);
	const value = useAuthProvider();
	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const checkAuthExpiration = (): boolean => {
	const { userPoolClientId } = COGNITO_CONFIG;
	const LastAuthUser = localStorage.getItem(
		`CognitoIdentityServiceProvider.${userPoolClientId}.LastAuthUser`,
	);
	if (!LastAuthUser) {
		return false;
	}
	const accessToken = localStorage.getItem(
		`CognitoIdentityServiceProvider.${userPoolClientId}.${LastAuthUser}.accessToken`,
	);
	if (!accessToken) {
		return false;
	}
	const decodedAccessToken = decodeJWT(accessToken);
	const tokenExpiration = decodedAccessToken.payload.exp;
	if (tokenExpiration === undefined) {
		return false;
	}
	if (Date.now() > tokenExpiration * 1000) {
		localStorage.removeItem(`CognitoIdentityServiceProvider.${userPoolClientId}.LastAuthUser`);
		localStorage.removeItem(
			`CognitoIdentityServiceProvider.${userPoolClientId}.${LastAuthUser}.accessToken`,
		);
		return false;
	}
	return true;
};

const useAuthProvider = (): UseAuth => {
	const [email, setEmail] = useState<string>("");
	const [authUser, setAuthUser] = useState<AuthUser | null>(null);
	const [usergroup, setUsergroup] = useState<string>("");
	const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

	useEffect(() => {
		setIsAuthenticated(checkAuthExpiration());
	}, []);

	/**
	 * サインイン
	 * @param {SignInInput} { username, password }
	 * @description サインイン処理を行う
	 * @returns {Promise<SignInOutput>}
	 */
	const handleSignIn = useCallback(
		async ({ username, password }: SignInInput): Promise<SignInOutput> => {
			try {
				const { isSignedIn, nextStep } = await signIn({ username, password });
				setIsAuthenticated(isSignedIn);
				if (isSignedIn) {
					const currentUser = await getCurrentUser();
					const { signInDetails } = await getCurrentUser();
					const loginId = signInDetails?.loginId || "";
					setEmail(loginId);
					setAuthUser(currentUser);
					const session = await fetchAuthSession();
					const idToken = session.tokens?.idToken?.toString();
					const COGNITO_IDP = `cognito-idp.${import.meta.env.VITE_AWS_REGION}.amazonaws.com/${import.meta.env.VITE_COGNITO_USERPOOL_ID}`;
					if (!idToken) {
						throw new Error("ID Token is undefined");
					}
					const loginData = {
						[COGNITO_IDP]: idToken,
					};
					const client = new CognitoIdentityProviderClient({
						region: import.meta.env.VITE_AWS_REGION,
						credentials: fromCognitoIdentityPool({
							clientConfig: { region: import.meta.env.VITE_AWS_REGION },
							identityPoolId: COGNITO_IDPOOL_ID || "",
							logins: loginData,
						}),
					});
					const input = {
						Username: loginId,
						UserPoolId: import.meta.env.VITE_COGNITO_USERPOOL_ID,
						Limit: 10,
					};
					const ListUserGrpCommand = new AdminListGroupsForUserCommand(input);
					const ListUserGrpResponse = await client.send(ListUserGrpCommand);
					const userGroup = ListUserGrpResponse.Groups?.map((group) => group.GroupName);
					if (userGroup?.includes("admins")) {
						setUsergroup("管理者");
					} else if (userGroup?.includes("users")) {
						setUsergroup("実行者");
					}
				}
				return { isSignedIn, nextStep };
			} catch (error: unknown) {
				if (!(error instanceof Error)) throw new Error(error as string);
				throw error;
			}
		},
		[],
	);

	/**
	 * 初回のサインイン認証とパスワードの更新
	 * @param {ConfirmSignInInput} { username, tempPassword, newPassword, newConfirmPassword }
	 * @description 初回のサインイン認証とパスワードの更新処理を行う
	 * @returns {Promise<ConfirmSignInOutput>}
	 */
	const handleConfirmSignIn = useCallback(
		async ({ username, tempPassword, newPassword, newConfirmPassword }: ConfirmSignInInput) => {
			try {
				if (newPassword !== newConfirmPassword) {
					const error = new Error(cognitoErrorMessages.ConfirmSignIn.PasswordMismatch);
					error.name = cognitoErrorNames.ConfirmSignIn.PasswordMismatch;
					throw error;
				}
				await signIn({ username, password: tempPassword });
				const res = await confirmSignIn({
					challengeResponse: newPassword,
				});
				return res;
			} catch (error: unknown) {
				if (!(error instanceof Error)) throw new Error(error as string);
				throw error;
			}
		},
		[],
	);

	/**
	 * サインアウト
	 * @description サインアウト（ログアウト）処理を行う
	 * @returns {Promise<void>}
	 */
	const handleSignOut = useCallback(async (): Promise<void> => {
		try {
			await signOut();
			setIsAuthenticated(false);
			setAuthUser(null);
		} catch (error: unknown) {
			if (!(error instanceof Error)) throw new Error(error as string);
			throw error;
		}
	}, []);

	/**
	 * パスワードリセットの開始
	 * @param {ResetPasswordInput} { username }
	 * @description パスワードリセットを開始する
	 * @returns {Promise<void>}
	 */
	const handleResetPassword = useCallback(async ({ username }: ResetPasswordInput) => {
		try {
			const response = await resetPassword({ username });
			return response;
		} catch (error: unknown) {
			if (!(error instanceof Error)) throw new Error(error as string);
			throw error;
		}
	}, []);

	/**
	 * パスワードリセット
	 * @param {ConfirmResetPasswordInput} { username, confirmationCode, newPassword }
	 * @description 確認コードと新しいパスワードでパスワードをリセットする
	 * @returns {Promise<void>}
	 */
	const handleConfirmResetPassword = useCallback(
		async ({ username, confirmationCode, newPassword }: ConfirmResetPasswordInput) => {
			try {
				await confirmResetPassword({ username, confirmationCode, newPassword });
			} catch (error: unknown) {
				if (!(error instanceof Error)) throw new Error(error as string);
				throw error;
			}
		},
		[],
	);

	/**
	 * 認証済みユーザーの取得
	 * @description 認証済みユーザーを取得する
	 * @returns {Promise<CurrentAuthenticatedUser | undefined>}
	 */
	const currentAuthenticatedUser = useCallback(async () => {
		try {
			const currentUser = await getCurrentUser();
			const { signInDetails } = await getCurrentUser();
			const { tokens } = await fetchAuthSession();
			const loginId = signInDetails?.loginId || "";
			const jwt = tokens?.idToken;
			if (!jwt) throw new Error("id token not found");
			setEmail(loginId);
			setEmail(signInDetails?.loginId || "");
			setAuthUser(currentUser);
			setIsAuthenticated(!!currentUser.userId);
			const COGNITO_IDP = `cognito-idp.${import.meta.env.VITE_AWS_REGION}.amazonaws.com/${import.meta.env.VITE_COGNITO_USERPOOL_ID}`;
			if (!jwt) {
				throw new Error("ID Token is undefined");
			}
			const loginData = {
				[COGNITO_IDP]: jwt.toString(),
			};
			const client = new CognitoIdentityProviderClient({
				region: import.meta.env.VITE_AWS_REGION,
				credentials: fromCognitoIdentityPool({
					clientConfig: { region: import.meta.env.VITE_AWS_REGION },
					identityPoolId: COGNITO_IDPOOL_ID || "",
					logins: loginData,
				}),
			});
			const input = {
				Username: loginId,
				UserPoolId: import.meta.env.VITE_COGNITO_USERPOOL_ID,
				Limit: 10,
			};
			const ListUserGrpCommand = new AdminListGroupsForUserCommand(input);
			const ListUserGrpResponse = await client.send(ListUserGrpCommand);
			const userGroup = ListUserGrpResponse.Groups?.map((group) => group.GroupName);
			if (userGroup?.includes("admins")) {
				setUsergroup("管理者");
			} else if (userGroup?.includes("users")) {
				setUsergroup("実行者");
			}
			return { authUser: currentUser, isAuthenticated: !!currentUser.userId };
		} catch (error: unknown) {
			if (!(error instanceof Error)) throw new Error(error as string);
			if (error.name !== cognitoErrorNames.GetCurrentUser.UserUnAuthenticatedException) {
				throw error;
			}
		}
	}, []);

	useEffect(() => {
		if (isAuthenticated) {
			currentAuthenticatedUser();
		}
	}, [isAuthenticated, currentAuthenticatedUser]);

	/**
	 * クレデンシャルの取得
	 * @returns {Promise<GetCredentialsForIdentityCommandOutput>}
	 */
	const fetchCredentials = useCallback(async () => {
		try {
			const { region, userPoolId } = COGNITO_CONFIG;
			const { tokens } = await fetchAuthSession({ forceRefresh: true });
			const jwt = tokens?.idToken;
			if (!jwt) throw new Error("Id token not found");
			const cognitoidentity = new CognitoIdentity({ region });
			const jwtString = jwt.toString();

			const { IdentityId } = await cognitoidentity.getId({
				IdentityPoolId: COGNITO_IDPOOL_ID || "",
				Logins: {
					[`cognito-idp.ap-northeast-1.amazonaws.com/${userPoolId}`]: jwtString,
				},
			});
			if (!IdentityId) throw new Error("can not fetch identityId");
			const response = await cognitoidentity.getCredentialsForIdentity({
				IdentityId,
				Logins: {
					[`cognito-idp.ap-northeast-1.amazonaws.com/${userPoolId}`]: jwtString,
				},
			});
			return response;
		} catch (error: unknown) {
			if (!(error instanceof Error)) throw new Error(error as string);
			throw error;
		}
	}, []);

	return {
		email,
		authUser,
		usergroup,
		isAuthenticated,
		handleSignIn,
		handleConfirmSignIn,
		handleResetPassword,
		handleConfirmResetPassword,
		handleSignOut,
		currentAuthenticatedUser,
		fetchCredentials,
	};
};
